import React from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { useIntl } from 'react-intl';
import numeral from 'numeral';
import { currencySymbol } from '../../../../../utils/formatters';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend
} from 'recharts';

const SuccessionBuyerPerspective = ({ result, query, company, index = 1 }) => {
  const intl = useIntl();
  const buyerCashFlow = get(result, `cashFlow.illustration.buyer${index}CashFlow`, []);
  const buyBackYear = get(result, `cashFlow.buyer${index}BreakEven`, -1);
  const cashFlowAnalysisWord = get(company, 'cash_flow_analysis_word', 'Cash Flow Analysis');
  
  // Feature flags - using the same pattern as original components
  const features = get(company, 'company.data.features', []);
  const showCheckSwap = features.includes('check_swap');
  const showSellerNote = features.includes('cashflow_seller_note');
  const showOrdinaryTaxes = features.includes('cash_flow_ordinary_income_tax_rates');
  
  // Formatting
  const dollarFormat = '0,0.00';
  const percentFormat = '0%';
  const decimalPercentFormat = '0.0%';

  const formatCurrency = (value) => {
    const sym = currencySymbol();
    if (!value || isNaN(value)) return `${sym}0.00`;
    return `${sym}${numeral(value).format(dollarFormat)}`;
  };

  const formatPercentage = (value, useDecimal = false) => {
    if (!value || isNaN(value)) return '0%';
    return numeral(value).format(useDecimal ? decimalPercentFormat : percentFormat);
  };

  // Get payout word
  const payoutWord = get(result, 'company.cashflow_payout_word', 'Payout');
  
  // Chart data for Recharts
  const chartData = buyerCashFlow.map((cf, index) => ({
    year: `${intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.year_label' })} ${index + 1}`,
    yearNumber: index + 1,
    freeCashFlow: cf.freeCashFlow || 0,
    isBreakEven: buyBackYear === index + 1
  }));

  return (
    <div className="bg-succession-bg-primary succession-buyer-perspective">
      {/* Print-specific styles */}
      <style jsx>{`
        @media print {
          .recharts-wrapper, .recharts-responsive-container {
            display: none !important;
          }
          table {
            font-size: 8px !important;
          }
          th, td {
            padding: 2px 4px !important;
            font-size: 8px !important;
          }
        }
      `}</style>
      
      {/* Chart Section */}
      <div className="mb-8 chart-section">
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 20, right: 30, left: 80, bottom: 5 }}>
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="var(--succession-border)"
              />
              <XAxis 
                dataKey="year"
                tick={{ fill: 'var(--succession-text-secondary)' }}
                stroke="var(--succession-border)"
              />
              <YAxis 
                tickFormatter={(value) => formatCurrency(value)}
                tick={{ fill: 'var(--succession-text-secondary)' }}
                stroke="var(--succession-border)"
              />
              <Tooltip
                formatter={(value) => [formatCurrency(value), intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.chart_free_cash_flow' })]}
                labelFormatter={(label) => label}
                contentStyle={{
                  backgroundColor: 'var(--succession-bg-primary)',
                  border: '1px solid var(--succession-border)',
                  borderRadius: '6px',
                  color: 'var(--succession-text-primary)'
                }}
                labelStyle={{ color: 'var(--succession-text-primary)' }}
                itemStyle={{ color: 'var(--succession-text-primary)' }}
              />
              <Legend
                wrapperStyle={{ color: 'var(--succession-text-primary)' }}
              />
              <Line
                type="monotone"
                dataKey="freeCashFlow"
                stroke="var(--succession-primary)"
                strokeWidth={2}
                name={intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.chart_free_cash_flow' })}
                dot={(props) => {
                  const { cx, cy, payload } = props;
                  if (payload && payload.isBreakEven) {
                    return (
                      <circle 
                        cx={cx} 
                        cy={cy} 
                        r={6} 
                        fill="var(--succession-accent)" 
                        stroke="var(--succession-primary)" 
                        strokeWidth={2}
                      />
                    );
                  }
                  return <circle cx={cx} cy={cy} r={3} fill="var(--succession-primary)" />;
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        {buyBackYear > 0 && (
          <div className="mt-4 text-sm text-succession-text-secondary">
            <span className="inline-block w-3 h-3 bg-succession-accent rounded-full mr-2"></span>
            {intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.break_even_note' })}
          </div>
        )}
      </div>

      {/* Data Table */}
      <div className="overflow-x-auto printable-table">
        <table className="w-full border-collapse border border-succession-border succession-table">
          <thead>
            <tr className="bg-succession-bg-secondary">
              <th colSpan={buyerCashFlow.length + 1} className="px-4 py-3 text-left font-semibold text-succession-text-primary border">
                {intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.table_header' }, { index })}
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Year Headers */}
            <tr>
              <td className="px-4 py-2 font-semibold text-succession-text-primary border">
                <strong>{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.year_label' })}</strong>
              </td>
              {buyerCashFlow.map((cf, i) => (
                <td 
                  key={i} 
                  className={`px-4 py-2 text-right font-semibold border ${
                    buyBackYear === i + 1 ? 'bg-succession-accent' : ''
                  }`}
                >
                  <strong>{i + 1}{buyBackYear === i + 1 ? '*' : ''}</strong>
                </td>
              ))}
            </tr>

            {/* Structure depends on ordinary vs non-ordinary feature flag */}
            {showOrdinaryTaxes ? (
              // Ordinary Structure (detailed production breakdown)
              <>
                {/* Revenue Growth */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border italic">{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_revenue_growth' })}</td>
                  {buyerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                      {formatPercentage(cf.revenueGrowth)}
                    </td>
                  ))}
                </tr>

                {/* Personal Production */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_personal_production' })}</td>
                  {buyerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                      {formatCurrency(cf.personalProduction)}
                    </td>
                  ))}
                </tr>

                {/* Acquired Production */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_acquired_production' })}</td>
                  {buyerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                      {formatCurrency(cf.aquiredProduction)}
                    </td>
                  ))}
                </tr>

                {/* Total Production */}
                <tr>
                  <td className="px-4 py-2 font-semibold text-succession-text-primary border">
                    <strong>{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_total_production' })}</strong>
                  </td>
                  {buyerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right font-semibold text-succession-text-primary border">
                      <strong>{formatCurrency(cf.totalProduction)}</strong>
                    </td>
                  ))}
                </tr>

                {/* Payout Rate */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_payout_rate' }, { payoutWord })}</td>
                  {buyerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                      {formatPercentage(cf.payoutRate, true)}
                    </td>
                  ))}
                </tr>

                {/* Payout */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_payout' }, { payoutWord })}</td>
                  {buyerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                      {formatCurrency(cf.payout)}
                    </td>
                  ))}
                </tr>

                {/* Retirement Choice - RJA specific */}
                {typeof document !== 'undefined' && document.location.href.match('rja') && (
                  <tr>
                    <td className="px-4 py-2 font-semibold text-succession-text-primary border">
                      <strong>{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_retirement_choice' })}</strong>
                    </td>
                    <td className="px-4 py-2 border"></td>
                  </tr>
                )}
              </>
            ) : (
              // Non-Ordinary Structure (simplified revenue structure)
              <>
                {/* Revenue Growth */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border italic">{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_revenue_growth' })}</td>
                  {buyerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                      {formatPercentage(cf.revenueGrowth)}
                    </td>
                  ))}
                </tr>

                {/* Attrition (only Year 1) */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_attrition' })}</td>
                  {buyerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                      {i === 0 ? formatPercentage(cf.attritionRate) : ''}
                    </td>
                  ))}
                </tr>

                {/* Gross Revenue */}
                <tr>
                  <td className="px-4 py-2 font-semibold text-succession-text-primary border">
                    <strong>{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_gross_revenue' })}</strong>
                  </td>
                  {buyerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right font-semibold text-succession-text-primary border">
                      <strong>{formatCurrency(cf.grossRevenue)}</strong>
                    </td>
                  ))}
                </tr>

                {/* Expenses Section Header */}
                <tr>
                  <td colSpan={2} className="px-4 py-2 font-semibold text-succession-text-primary border">
                    <strong>{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_expenses' })}</strong>
                  </td>
                </tr>

                {/* Branch Retention Rate */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_payout_rate' }, { payoutWord })}</td>
                  {buyerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                      {formatPercentage(query.bdRetentionRate, true)}
                    </td>
                  ))}
                </tr>

                {/* Branch Retention (negative) */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_payout' }, { payoutWord })}</td>
                  {buyerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-error border">
                      {formatCurrency(-(cf.payout))}
                    </td>
                  ))}
                </tr>

                {/* Bold HR above Net Income */}
                <tr>
                  <td colSpan={buyerCashFlow.length + 1} className="border-b-4 border-succession-border p-0"></td>
                </tr>

                {/* Net Income */}
                <tr>
                  <td className="px-4 py-2 font-bold text-succession-text-primary border bg-succession-bg-secondary">
                    <strong>{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_net_income' })}</strong>
                  </td>
                  {buyerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right font-bold text-succession-text-primary border bg-succession-bg-secondary">
                      <strong>{formatCurrency(cf.netIncome)}</strong>
                    </td>
                  ))}
                </tr>

                {/* Branch Expenses */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_branch_expenses_1' })}</td>
                  {buyerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-error border">
                      {formatCurrency(cf.branchExpenses)}
                    </td>
                  ))}
                </tr>

                {/* Consulting Agreement */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_consulting_agreement' })}</td>
                  {buyerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-error border">
                      {formatCurrency(-(cf.consultingAgreement))}
                    </td>
                  ))}
                </tr>
              </>
            )}

            {/* Payment section structure depends on ordinary vs non-ordinary feature flag */}
            {showOrdinaryTaxes ? (
              // Ordinary Structure (detailed payment breakdown)
              <>
                {/* Book Buy Payment Total */}
                <tr>
                  <td className="px-4 py-2 font-semibold text-succession-text-primary border">
                    <strong>{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_book_buy_payment' })}</strong>
                  </td>
                  {buyerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-error font-semibold border">
                      <strong>{formatCurrency(-(cf.bookBuyPaymentTotal || 0))}</strong>
                    </td>
                  ))}
                </tr>

                {/* Fixed Payment */}
                <tr>
                  <td className="pl-5 pr-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_fixed_payment' })}</td>
                  {buyerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-error border">
                      {formatCurrency(-(cf.fixedPayment || 0))}
                    </td>
                  ))}
                </tr>

                {/* Contingent Participation Payment */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_contingent_participation' })}</td>
                  {buyerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-error border">
                      {formatCurrency(-(cf.contingentPayment || 0))}
                    </td>
                  ))}
                </tr>

                {/* Check Swap - CONDITIONAL based on feature flag */}
                {showCheckSwap && (
                  <>
                    <tr>
                      <td className="px-4 py-2 font-semibold text-succession-text-primary border">
                        <strong>{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_check_swap' })}</strong>
                      </td>
                      <td className="px-4 py-2 border"></td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_transition_assistance_bonus' })}</td>
                      {buyerCashFlow.map((cf, i) => (
                        <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                          {formatCurrency(cf.transitionAssistanceBonus || 0)}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="px-4 py-2 font-semibold text-succession-text-primary border">
                        <strong>{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_branch_expenses_2' })}</strong>
                      </td>
                      {buyerCashFlow.map((cf, i) => (
                        <td key={i} className="px-4 py-2 text-right text-succession-error border">
                          {formatCurrency(cf.branchExpenses || 0)}
                        </td>
                      ))}
                    </tr>
                  </>
                )}

                {/* Consulting Agreement - show if feature enabled */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_consulting_agreement' })}</td>
                  {buyerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-error border">
                      {formatCurrency(cf.consultingAgreement || 0)}
                    </td>
                  ))}
                </tr>
              </>
            ) : (
              // Non-Ordinary Structure - no payments section here, it comes after taxes
              <></>
            )}

            {/* Bold HR above Pretax Net Income */}
            <tr>
              <td colSpan={buyerCashFlow.length + 1} className="border-b-4 border-succession-border p-0"></td>
            </tr>

            {/* Pretax Net Income */}
            <tr>
              <td className="px-4 py-2 font-bold text-succession-text-primary border bg-succession-bg-secondary">
                <strong>{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_pretax_net_income' })}</strong>
              </td>
              {buyerCashFlow.map((cf, i) => (
                <td key={i} className="px-4 py-2 text-right font-bold text-succession-text-primary border bg-succession-bg-secondary">
                  <strong>{formatCurrency(cf.pretaxNetIncome)}</strong>
                </td>
              ))}
            </tr>

            {/* Tax structure based on ordinary vs non-ordinary feature flag */}
            {showOrdinaryTaxes ? (
              // Ordinary Tax Structure (simplified)
              <tr>
                <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_taxes_35' })}</td>
                {buyerCashFlow.map((cf, i) => (
                  <td key={i} className="px-4 py-2 text-right text-succession-error border">
                    {formatCurrency(-(cf.taxes || 0))}
                  </td>
                ))}
              </tr>
            ) : (
              // Non-Ordinary Tax Structure (detailed breakdown)
              <>
                {/* Tax Adjustments Section Header */}
                <tr>
                  <td colSpan={2} className="px-4 py-2 font-semibold text-succession-text-primary border bg-succession-bg-secondary">
                    <strong>{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_tax_adjustments' })}</strong>
                  </td>
                </tr>

                {/* Down Payment Loan Interest */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_down_payment_loan_interest' })}</td>
                  {buyerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                      {formatCurrency(cf.downpaymentLoanInterestTax || 0)}
                    </td>
                  ))}
                </tr>

                {/* Down Payment Amortization */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_down_payment_amortization' })}</td>
                  {buyerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                      {formatCurrency(cf.downpaymentAmortizationTax || 0)}
                    </td>
                  ))}
                </tr>

                {/* Earn Out */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_earn_out' })}</td>
                  {buyerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-error border">
                      {formatCurrency(-(cf.contingentPayment || 0))}
                    </td>
                  ))}
                </tr>

                {/* Seller Note breakdown - only show if feature enabled */}
                {showSellerNote && (
                  <>
                    <tr>
                      <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_seller_note_interest' })}</td>
                      {buyerCashFlow.map((cf, i) => (
                        <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                          {formatCurrency(cf.sellerNoteInterestPayment || 0)}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_seller_note_amortization' })}</td>
                      {buyerCashFlow.map((cf, i) => (
                        <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                          {formatCurrency(-(cf.sellerNoteAmortization || 0))}
                        </td>
                      ))}
                    </tr>
                  </>
                )}

                {/* Bold HR above Taxable Income */}
                <tr>
                  <td colSpan={buyerCashFlow.length + 1} className="border-b-4 border-succession-border p-0"></td>
                </tr>

                {/* Taxable Income */}
                <tr>
                  <td className="px-4 py-2 font-bold text-succession-text-primary border">
                    <strong>{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_taxable_income' })}</strong>
                  </td>
                  {buyerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right font-bold text-succession-text-primary border">
                      <strong>{formatCurrency(cf.taxableIncome || 0)}</strong>
                    </td>
                  ))}
                </tr>

                {/* Taxes (35%) */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">
                    {intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_taxes_rate' }, { rate: formatPercentage(buyerCashFlow[0]?.taxRate || 0.35) })}
                  </td>
                  {buyerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-error border">
                      {formatCurrency(-(cf.taxes || 0))}
                    </td>
                  ))}
                </tr>
              </>
            )}

            {/* Bold HR above After Tax Net Income */}
            <tr>
              <td colSpan={buyerCashFlow.length + 1} className="border-b-4 border-succession-border p-0"></td>
            </tr>

            {/* After Tax Net Income */}
            <tr>
              <td className="px-4 py-2 font-bold text-succession-text-primary border bg-succession-bg-secondary">
                <strong>{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_after_tax_net_income' })}</strong>
              </td>
              {buyerCashFlow.map((cf, i) => (
                <td key={i} className="px-4 py-2 text-right font-bold text-succession-text-primary border bg-succession-bg-secondary">
                  <strong>{formatCurrency(cf.afterTaxNetIncome)}</strong>
                </td>
              ))}
            </tr>

            {/* Final sections structure depends on ordinary vs non-ordinary feature flag */}
            {showOrdinaryTaxes ? (
              // Ordinary Structure
              <>
                {/* Lump Sum Payment (Out of Pocket) */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_lump_sum_out_of_pocket' })}</td>
                  {buyerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-error border">
                      {formatCurrency(-(cf.lumpSumPaymentOutOfPocket || 0))}
                    </td>
                  ))}
                </tr>

                {/* Bold HR above Debt Service */}
                <tr>
                  <td colSpan={buyerCashFlow.length + 1} className="border-b-4 border-succession-border p-0"></td>
                </tr>

                {/* Debt Service Header */}
                <tr>
                  <td className="px-4 py-2 font-bold text-succession-text-primary border bg-succession-bg-secondary">
                    <strong>{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_debt_service' })}</strong>
                  </td>
                  <td className="px-4 py-2 border bg-succession-bg-secondary"></td>
                </tr>

                {/* Transition Assistance Loan Repayment */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_transition_assistance_repayment' })}</td>
                  {buyerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-error border">
                      {formatCurrency(-(cf.transitionAssistanceBonus || 0))}
                    </td>
                  ))}
                </tr>

                {/* Succession Loan Repayment */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_succession_loan_repayment' })}</td>
                  {buyerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-error border">
                      {formatCurrency(-(cf.successionLoanRepayment || 0))}
                    </td>
                  ))}
                </tr>
              </>
            ) : (
              // Non-Ordinary Structure
              <>
                {/* Payments To Seller Section Header */}
                <tr>
                  <td colSpan={2} className="px-4 py-2 font-semibold text-succession-text-primary border">
                    <strong>{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_payments_to_seller' })}</strong>
                  </td>
                </tr>

                {/* Down Payment Expense (Cash) */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_down_payment_cash' })}</td>
                  {buyerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-error border">
                      {formatCurrency(-(cf.lumpSumPaymentOutOfPocket || 0))}
                    </td>
                  ))}
                </tr>

                {/* Earn Out Expense */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_earn_out_expense' })}</td>
                  {buyerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-error border">
                      {formatCurrency(-(cf.contingentPayment || 0))}
                    </td>
                  ))}
                </tr>

                {/* Bold HR above Debt Service */}
                <tr>
                  <td colSpan={buyerCashFlow.length + 1} className="border-b-4 border-succession-border p-0"></td>
                </tr>

                {/* Debt Service Header */}
                <tr>
                  <td className="px-4 py-2 font-bold text-succession-text-primary border bg-succession-bg-secondary">
                    <strong>{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_debt_service' })}</strong>
                  </td>
                  <td className="px-4 py-2 border bg-succession-bg-secondary"></td>
                </tr>

                {/* Down Payment Note */}
                <tr>
                  <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_down_payment_note' })}</td>
                  {buyerCashFlow.map((cf, i) => (
                    <td key={i} className="px-4 py-2 text-right text-succession-error border">
                      {formatCurrency(-(cf.downPaymentNote || 0))}
                    </td>
                  ))}
                </tr>

                {/* Seller Note - only show if feature enabled */}
                {showSellerNote && (
                  <tr>
                    <td className="px-4 py-2 text-succession-text-primary border">{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_seller_note' })}</td>
                    {buyerCashFlow.map((cf, i) => (
                      <td key={i} className="px-4 py-2 text-right text-succession-text-primary border">
                        {formatCurrency(cf.sellerNotePayment || 0)}
                      </td>
                    ))}
                  </tr>
                )}
              </>
            )}

            {/* Gap row */}
            <tr>
              {buyerCashFlow.map((cf, i) => (
                <td key={i} className="h-1"></td>
              ))}
            </tr>

            {/* Bold HR above Free Cash Flow */}
            <tr>
              <td colSpan={buyerCashFlow.length + 1} className="border-b-4 border-succession-border p-0"></td>
            </tr>

            {/* Free Cash Flow */}
            <tr>
              <td className="px-4 py-2 font-bold text-succession-text-primary border bg-succession-bg-secondary">
                <strong>{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_free_cash_flow' })}</strong>
              </td>
              {buyerCashFlow.map((cf, i) => (
                <td
                  key={i}
                  className={`px-4 py-2 text-right border bg-succession-bg-secondary ${
                    cf.freeCashFlow > -0.01 ? 'font-bold text-succession-text-primary' : 'font-bold text-succession-error'
                  }`}
                >
                  <strong>{formatCurrency(cf.freeCashFlow)}</strong>
                </td>
              ))}
            </tr>

            {/* Bold HR beneath Free Cash Flow */}
            <tr>
              <td colSpan={buyerCashFlow.length + 1} className="border-b-4 border-succession-border p-0"></td>
            </tr>

            {/* Debt Service Coverage Ratio */}
            <tr className="bg-succession-bg-secondary">
              <td className="px-4 py-2 text-succession-text-primary border bg-succession-bg-secondary italic">
                &nbsp;&nbsp;&nbsp;<em>{intl.formatMessage({ id: 'succession.cash_flow_analysis.buyer_perspective.row_debt_service_coverage' })}</em>
              </td>
              {buyerCashFlow.map((cf, i) => (
                <td key={i} className="px-4 py-2 text-right text-succession-text-primary border bg-succession-bg-secondary font-bold">
                  <strong>{numeral(cf.debtServiceCoverageRatio).format('0.00')}</strong>
                </td>
              ))}
            </tr>

            {/* Lighter HR beneath Debt Service Coverage Ratio */}
            <tr>
              <td colSpan={buyerCashFlow.length + 1} className="border-b-2 border-succession-border p-0"></td>
            </tr>

          </tbody>
        </table>
      </div>
    </div>
  );
};

const mapStateToProps = (state, props) => {
  return {
    result: { ...state.cfa.result },
    query: { ...state.cfa.query },
    company: { ...get(state, 'company', {}), ...get(state, 'notes.company', {}) },
  };
};

export default connect(mapStateToProps)(SuccessionBuyerPerspective); 